import classNames from 'classnames'
import { CSSProperties } from 'react'

// Domains
import Button from '@src/quince-site-common/src/domains/Button'
import Image from '@src/quince-site-common/src/domains/Image'
import RichText from '@src/quince-site-common/src/domains/RichText'

// Components
import { RichText as RichTextClass, RichTextProps } from '../../molecules'
import ButtonC from '../button'
import { Image as ImageClass, ImageProps } from '../image'
import { Link } from '../link'

// Styles
import styles from './product-comparision-column.module.scss'
import { ComparisionColumn } from './product-comparison-section-new.types'

const ProductColumn = ({
  column,
  wrapperStyle,
  tracker,
  columnIdx,
}: {
  column: ComparisionColumn
  wrapperStyle?: CSSProperties
  tracker: string
  columnIdx: number
}) => {
  const { button } = column
  const buttonProps = button && Button.makeButtonProps(button)

  const shouldWrapColumnInLink = buttonProps?.href

  const columnEl = (
    <div className={classNames(styles.column, shouldWrapColumnInLink && styles.column__withLink)} style={wrapperStyle}>
      {column.rowData.map(rowData => {
        const { headline, header, content, spotlightImage, iconImage, id } = rowData
        const isProductImage = !!spotlightImage

        return (
          <div className={classNames(styles.column__cell)} key={id}>
            <div className={styles.column__cell__contentWrapper}>
              {isProductImage ? (
                // Product image, with title and description
                <>
                  {spotlightImage && getImage(spotlightImage)}
                  {headline && (
                    <div
                      className={classNames(
                        styles.column__cell__contentWrapper__headline,
                        content && styles.column__cell__contentWrapper__headline__withContent,
                      )}
                    >
                      {getColumnRichText(headline)}
                    </div>
                  )}
                  {content && (
                    <p className={styles.column__cell__contentWrapper__content}>{getColumnRichText(content)}</p>
                  )}
                </>
              ) : (
                // Other specifications
                <div className={styles.column__cell__contentWrapper__container}>
                  {iconImage && getImage(iconImage, true)}
                  <div>
                    {header && (
                      <header className={styles.column__cell__contentWrapper__header}>
                        {getColumnRichText(header)}
                      </header>
                    )}
                    {content && (
                      <p className={styles.column__cell__contentWrapper__content}>{getColumnRichText(content)}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )
      })}
      {buttonProps && (
        <ButtonC
          {...buttonProps}
          className={styles.column__button}
          trackConfig={{
            trackGA: true,
            trackInternal: true,
            category: 'navigation',
            action: 'click_product_comparison_section_button',
            label: `${tracker}__button`,
            value: buttonProps.href,
            context: {
              index: columnIdx,
            },
          }}
          onClick={
            shouldWrapColumnInLink && window.location.href.includes(buttonProps.href)
              ? () => {
                  window.scrollTo({ top: 0, behavior: 'smooth' })
                }
              : undefined
          }
        />
      )}
    </div>
  )

  return shouldWrapColumnInLink ? (
    <Link
      href={buttonProps.href}
      onClick={
        window.location.href.includes(buttonProps.href)
          ? () => {
              window.scrollTo({ top: 0, behavior: 'smooth' })
            }
          : undefined
      }
      className={styles.linkForColumn}
      trackConfig={{
        trackGA: true,
        trackInternal: true,
        category: 'navigation',
        action: 'click_product_comparison_section_link',
        label: `${tracker}__column`,
        value: buttonProps.href,
        context: {
          index: columnIdx,
        },
      }}
    >
      {columnEl}
    </Link>
  ) : (
    columnEl
  )
}

const getImage = (image: Image | undefined, isIcon = false): React.ReactNode => {
  if (!image) {
    return null
  }

  const imageProps: ImageProps = Image.makeImageProps(image)

  return (
    <ImageClass
      {...imageProps}
      className={classNames(
        styles.column__cell__contentWrapper__image,
        isIcon && styles.column__cell__contentWrapper__image__icon,
      )}
    />
  )
}

export const getColumnRichText = (columnText: RichText): React.ReactNode => {
  const columnRichTextProps: RichTextProps | undefined = RichText.makeRichTextProps(columnText)
  return columnText && columnRichTextProps && <RichTextClass {...columnRichTextProps} />
}

export const getRowHeaders = (rowHeaders: RichText[]) => {
  return (
    <div className={classNames(styles.column, styles.productcomparisontable__rowHeaderColumn)}>
      <div
        className={classNames(
          styles.column__cell,
          styles.productcomparisontable__rowHeaderColumn__cell,
          styles.productcomparisontable__rowHeaderColumn__cell__first,
        )}
      />
      {rowHeaders.map((rowHeader, rowIndex) => (
        <div
          className={classNames(
            styles.column__cell,
            styles.productcomparisontable__rowHeaderColumn__cell,
            rowIndex === rowHeaders.length - 1 && styles.column__cell__last,
          )}
          key={rowHeader.id}
        >
          {getColumnRichText(rowHeader)}
        </div>
      ))}
    </div>
  )
}

export default ProductColumn
